import { isPlatformBrowser } from "@angular/common";
import { inject, Injectable, PLATFORM_ID } from "@angular/core";

export type PendingCoupon = {
  eventId: number;
  code: string;
  savedAt: string;
};

const STORAGE_KEY = "yes7-pending-coupon";

/** Cupom capturado de ?cupom= na página do evento, até o checkout aplicar. */
@Injectable({ providedIn: "root" })
export class PendingCouponService {
  private readonly platformId = inject(PLATFORM_ID);

  captureFromQuery(eventId: number | string, code: string | null | undefined): PendingCoupon | null {
    const normalized = code?.toString().trim().toUpperCase();
    const id = Number(eventId);
    if (!normalized || !Number.isFinite(id)) {
      return null;
    }

    const pending: PendingCoupon = {
      eventId: id,
      code: normalized,
      savedAt: new Date().toISOString(),
    };
    this.write(pending);
    return pending;
  }

  getForEvent(eventId: number | string): PendingCoupon | null {
    const pending = this.read();
    if (!pending) return null;
    if (Number(pending.eventId) !== Number(eventId)) return null;
    return pending;
  }

  clear(eventId?: number | string): void {
    if (eventId == null) {
      this.remove();
      return;
    }
    const pending = this.read();
    if (pending && Number(pending.eventId) === Number(eventId)) {
      this.remove();
    }
  }

  private read(): PendingCoupon | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as PendingCoupon;
      if (!parsed?.code || !parsed?.eventId) return null;
      return {
        eventId: Number(parsed.eventId),
        code: String(parsed.code).trim().toUpperCase(),
        savedAt: parsed.savedAt || new Date().toISOString(),
      };
    } catch {
      return null;
    }
  }

  private write(pending: PendingCoupon): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const payload = JSON.stringify(pending);
    sessionStorage.setItem(STORAGE_KEY, payload);
    localStorage.setItem(STORAGE_KEY, payload);
  }

  private remove(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    sessionStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
  }
}
